<template>
  <component :is="!context.isMobile ? DropdownMenuItem : DrawerClose" as-child>
    <RouterLink
      :to="props.to"
      :data-emphasis-identifier="props.label.replace(/\s+/g, '-').toLowerCase()"
      :class="[
        'group/item',
        'relative flex shrink-0 cursor-pointer select-none items-center gap-3 self-stretch rounded-md border-none px-3 outline-none transition-colors duration-75',
        'text-left text-sm text-current',
        !context.isMobile ? 'py-1' : 'py-2',
        props.disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        props.variant === 'default' &&
          'active:bg-dark-muted data-[highlighted]:bg-dark-muted',
        props.variant === 'danger' &&
          'active:bg-red-600 data-[highlighted]:bg-red-600',
      ]"
    >
      <slot v-if="slots.icon" name="icon" />
      <component
        :is="iconMappingMicro[props.icon]"
        v-else-if="props.icon"
        :class="[
          'size-4 shrink-0 transition-colors',
          props.variant === 'default' && 'text-muted-foreground',
          props.variant === 'danger' &&
            'text-muted-foreground group-active/item:text-white group-data-[highlighted]/item:text-white',
        ]"
      />

      <span class="min-w-0 grow break-words">
        {{ props.label }}
      </span>
    </RouterLink>
  </component>
</template>

<script setup lang="ts">
import { DropdownMenuItem } from "reka-ui";
import { DrawerClose } from "vaul-vue";
import { RouterLink, useRouter } from "vue-router";

import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { useKeybinds } from "@/lib/hotkeys";
import type { RouteLocation } from "@/router/types";

import { inject } from "./context";

const context = inject();

const router = useRouter();

const emit = defineEmits<{
  (e: "click"): void;
}>();

export type Props = {
  label: string;
  description?: string;
  to: RouteLocation;
  hotkey?: string;
  disabled?: boolean;
  icon?: MappedIconMicro;
  variant?: "default" | "danger";
};

const props = withDefaults(defineProps<Props>(), {
  variant: "default",
});

const slots = defineSlots<{
  icon?(): unknown;
}>();

useKeybinds(() => {
  const hotkey = props.hotkey;
  if (!hotkey || props.disabled) {
    return;
  }

  return {
    [hotkey]: {
      name: props.description ?? props.label,
      group: `Menu`,
      run(ev) {
        if (ev.defaultPrevented) {
          return;
        }

        if (props.to) {
          router.push(props.to);
        } else {
          emit("click");
        }

        return true;
      },
    },
  };
});
</script>
